import { useEffect, useState } from "react";
import { AlertTriangle, X, Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { api } from "../../../lib/api";

export default function ImpactAnalysisModal({ kind, item, onClose, onConfirm }) {
    const [impact, setImpact] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!item) return;
        api.get(`/admin/master/${kind}/${item.id}/impact`)
            .then((r) => setImpact(r.data))
            .finally(() => setLoading(false));
    }, [kind, item]);

    if (!item) return null;

    const safe = impact?.safe_to_delete;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-5" data-testid="md-impact-modal">
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h3 className="font-heading text-lg font-bold text-[#0F172A] flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-500" /> Impact Analysis
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">Before deleting &quot;<span className="font-semibold">{item.value}</span>&quot; ({kind})</p>
                    </div>
                    <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
                </div>

                {loading && <div className="p-4 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Analyzing references…</div>}

                {!loading && impact && (
                    <div className="space-y-3">
                        <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${safe ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
                            {safe ? <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0" /> : <ShieldAlert className="w-5 h-5 mt-0.5 shrink-0" />}
                            <div>
                                <div className="font-bold">
                                    {safe ? "Safe to soft-delete" : "References exist — deletion will not block dropdowns, but historical data may still mention this value."}
                                </div>
                                <div className="text-xs mt-1 opacity-80">
                                    Soft delete moves to Recycle Bin. Restore anytime — no data loss.
                                </div>
                            </div>
                        </div>

                        {Object.keys(impact.children || {}).length > 0 && (
                            <Section title="Child Records (blocks deletion)">
                                {Object.entries(impact.children).map(([k, v]) => (
                                    <RefRow key={k} label={k} count={v} blocking={v > 0} />
                                ))}
                            </Section>
                        )}

                        <Section title="Cross-Collection References">
                            {Object.entries(impact.references || {}).map(([k, v]) => (
                                <RefRow key={k} label={k.replace(/_/g, " ")} count={v} />
                            ))}
                        </Section>
                    </div>
                )}

                <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
                    <button onClick={onClose} data-testid="md-impact-cancel" className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                    <button
                        onClick={() => { onConfirm?.(); onClose(); }}
                        data-testid="md-impact-confirm"
                        className="px-4 py-2 text-sm font-semibold rounded-lg bg-rose-600 text-white hover:bg-rose-700"
                    >
                        Move to Recycle Bin
                    </button>
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{title}</div>
            <div className="space-y-1">{children}</div>
        </div>
    );
}

function RefRow({ label, count, blocking }) {
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-slate-700 capitalize">{label}</span>
            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                blocking ? "bg-red-100 text-red-700" : count > 0 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"
            }`}>{count}</span>
        </div>
    );
}
